import React from 'react';

export interface DataGridColumn<T> {
  key: string;
  header: string;
  className?: string;
  render: (row: T, index: number) => React.ReactNode;
}

interface DataGridProps<T> {
  title?: string;
  description?: string;
  columns: DataGridColumn<T>[];
  rows: T[];
  emptyText: string;
  loading?: boolean;
  minWidthClassName?: string;
  toolbar?: React.ReactNode;
}

function DataGrid<T>({ title, description, columns, rows, emptyText, loading = false, minWidthClassName = 'min-w-full', toolbar }: DataGridProps<T>) {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {(title || description) && (
        <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
          {title ? <h3 className="text-lg font-black text-gray-900">{title}</h3> : null}
          {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
          {toolbar ? <div className="mt-4">{toolbar}</div> : null}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-gray-500">{emptyText}</div>
      ) : (
        <div className="overflow-auto">
          <table className={minWidthClassName}>
            <thead className="border-b border-gray-200 bg-white">
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={`px-4 py-3 text-left text-xs font-bold uppercase text-gray-600 ${column.className || ''}`}>
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-gray-50/50' : 'bg-white'}>
                  {columns.map((column) => (
                    <td key={column.key} className={`px-4 py-3 align-top text-sm text-gray-700 ${column.className || ''}`}>
                      {column.render(row, index)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DataGrid;
